/**
 * Content constants for the website
 * Easy to update without changing component code
 */

export const HERO_CONTENT = {
  title: "MATISA",
  subtitle: "is Live",
  tagline: "Singer and Guitarist",
  quote: '"Turning moments into music."',
  socialHandle: "@matisaislive",
  cta: {
    primary: "Book a Show",
    secondary: "Watch Performances",
  },
};

export const ABOUT_CONTENT = {
  title: "About Matisa",
  subtitle: "A voice that touches the soul, a guitar that speaks the language of the heart.",
  // Bio with highlighted phrases marked with **text**
  bioHighlights: [
    { text: "With over ", highlight: false },
    { text: "8+ years", highlight: true },
    { text: " of experience in live music, Matisa has performed at more than ", highlight: false },
    { text: "600+ shows", highlight: true },
    { text: ", gigs, and events — from intimate café sessions, premium restaurants, and live lounges to grand wedding receptions. A versatile ", highlight: false },
    { text: "singer & guitarist", highlight: true },
    { text: ", she effortlessly switches between ", highlight: false },
    { text: "Bollywood Classics", highlight: true },
    { text: ", ", highlight: false },
    { text: "Western Hits", highlight: true },
    { text: ", ", highlight: false },
    { text: "Soulful Marathi Melodies", highlight: true },
    { text: ", and ", highlight: false },
    { text: "High-Energy Bangers", highlight: true },
    { text: " that get the crowd moving. Whether it's a ", highlight: false },
    { text: "romantic acoustic set", highlight: false },
    { text: " or an ", highlight: false },
    { text: "electrifying performance", highlight: false },
    { text: " that lights up the stage, Matisa crafts an ", highlight: false },
    { text: "unforgettable atmosphere", highlight: false },
    { text: " tailored to every occasion.", highlight: false },
  ],
  bioExtended: "Known for her powerful vocals and captivating stage presence, Matisa connects with audiences of all ages and backgrounds. Her repertoire spans decades of music — from retro classics and Sufi soul to contemporary chart-toppers — ensuring every event feels personal and memorable.",
  availability: "Available for weddings, corporate events, restaurants, private parties, and festivals across India and beyond.",
  stats: [
    { value: "8+", label: "Years" },
    { value: "600+", label: "Shows" },
  ],
  features: [
    {
      icon: "mic",
      title: "Live Vocals",
      description: "Powerful, emotive delivery tailored to your event's mood.",
    },
    {
      icon: "music_note",
      title: "Acoustic Guitar",
      description: "Soulful guitar arrangements that fill every room with warmth.",
    },
    {
      icon: "library_music",
      title: "Versatile Repertoire",
      description: "Bollywood, Western, Marathi, Retro, Sufi & more.",
    },
  ],
};

// Signature tagline - identity anchor
export const SIGNATURE_USP = "Soulful vocals & acoustic magic for unforgettable moments";

// Performance formats
export const PERFORMANCE_FORMATS = [
  { id: "solo", name: "Solo Acoustic", icon: "person", description: "Intimate & personal" },
  { id: "duo", name: "Duo", icon: "group", description: "Guitar + Keys/Percussion" },
  { id: "band", name: "Live Band", icon: "groups", description: "Full stage energy" },
  { id: "unplugged", name: "Unplugged Set", icon: "favorite", description: "Perfect for weddings" },
];

// Social proof stats
export const SOCIAL_PROOF = {
  stats: [
    { value: "8+", label: "Years Live" },
    { value: "600+", label: "Shows" },
    { value: "50+", label: "Weddings" },
  ],
};

export const MUSIC_GENRES = [
  { id: "bollywood", name: "Bollywood", icon: "movie" },
  { id: "semi-classical", name: "Semi-Classical", icon: "self_improvement" },
  { id: "western-pop", name: "Western Pop", icon: "music_note" },
  { id: "indi-rock", name: "Indi-Rock", icon: "electric_bolt" },
  { id: "blues", name: "Blues", icon: "nightlife" },
  { id: "jazz", name: "Jazz", icon: "piano" },
  { id: "country", name: "Country", icon: "landscape" },
  { id: "rock-n-roll", name: "Rock n Roll", icon: "bolt" },
  { id: "alternative-rock", name: "Alternative Rock", icon: "album" },
  { id: "folk", name: "Folk", icon: "forest" },
  { id: "punk", name: "Punk", icon: "flash_on" },
  { id: "indian-regional", name: "Indian Regional Music", icon: "celebration" },
];

export const LANGUAGES = {
  fluent: [
    { code: "hi", name: "Hindi", native: "हिंदी" },
    { code: "en", name: "English", native: "English" },
    { code: "mr", name: "Marathi", native: "मराठी" },
  ],
  canPerform: [
    { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
    { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
    { code: "bn", name: "Bengali", native: "বাংলা" },
    { code: "goa", name: "Goan", native: "कोंकणी" },
    { code: "raj", name: "Rajasthani", native: "राजस्थानी" },
    { code: "kok", name: "Kokani", native: "कोंकणी" },
    { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
    { code: "te", name: "Telugu", native: "తెలుగు" },
    { code: "ta", name: "Tamil", native: "தமிழ்" },
    { code: "ga", name: "Gadhwali", native: "गढ़वाली" },
    { code: "bho", name: "Bhojpuri", native: "भोजपुरी" },
  ],
};

export const SHOW_TYPES = [
  {
    id: "weddings",
    icon: "favorite",
    title: "Weddings",
    description: "Acoustic romance for your special day",
    accentColor: "pink",
  },
  {
    id: "corporate",
    icon: "business_center",
    title: "Corporate",
    description: "High-energy sets for professionals",
    accentColor: "primary",
  },
  {
    id: "festivals",
    icon: "queue_music",
    title: "Festivals",
    description: "Electric performances for the masses",
    accentColor: "cyan",
  },
  {
    id: "private",
    icon: "home",
    title: "Private Parties",
    description: "Intimate sessions for memorable gatherings",
    accentColor: "gold",
  },
  {
    id: "lounge",
    icon: "restaurant",
    title: "Live Lounge",
    description: "Soulful vibes for restaurants & venues",
    accentColor: "primary",
  },
];

export const YOUTUBE_VIDEOS = [
  {
    id: 1,
    url: "https://youtube.com/shorts/A9n70SDEKz8?si=lymOhyy2a7z6ITMv",
    categories: ["live", "band"],
  },
  {
    id: 2,
    url: "https://youtube.com/shorts/unk9e0ky-G4?si=fs1P_dh_cY8pWpU-",
    categories: ["live", "cover"],
  },
  {
    id: 3,
    url: "https://youtube.com/shorts/prpiaBxG6yE?si=9xJSKK1G4NUh_pOp",
    categories: ["cover", "acoustic"],
  },
  {
    id: 4,
    url: "https://youtube.com/shorts/1OrEyf---1s?si=abv97RT7xwhOpFZ1",
    categories: ["live", "cover"],
  },
  {
    id: 5,
    url: "https://youtube.com/shorts/H1hl5CUy_TE?si=79xw8bVY_IOd7d9r",
    categories: ["band", "live"],
  },
  {
    id: 6,
    url: "https://youtube.com/shorts/woA8bJvtPiw?si=q-P3N0hO_o4Mt8oZ",
    categories: ["acoustic", "live"],
  },
  {
    id: 7,
    url: "https://youtube.com/shorts/qiXsSkPizKw?si=OkYdwaW7JNzcf3Vh",
    categories: ["live"],
  },
  {
    id: 8,
    url: "https://youtube.com/shorts/3yqXcDoNPGU?si=HaVyo9ZW1egym5u3",
    categories: ["cover", "live", "acoustic"],
  },
  {
    id: 9,
    url: "https://youtube.com/shorts/Ku6f8aFhmR4?si=atLpHiBwkDXM8F_m",
    categories: ["live", "cover"],
  },
  {
    id: 10,
    url: "https://youtube.com/shorts/bGcy7f4hOwc?si=7KY18u5z1QPQiazb",
    categories: ["live", "acoustic"],
  },
  {
    id: 11,
    url: "https://youtube.com/shorts/mVTHo2yVV9o?si=MYT-cBDNClq-2_yz",
    categories: ["live", "band"],
  },
  // Recording / Playback Singing Videos
  {
    id: 12,
    url: "https://youtu.be/BV-q_ZNIPWo?si=_7xnkoZM-2ggEMSQ",
    categories: ["recording"],
    isRecording: true,
  },
  {
    id: 13,
    url: "https://youtu.be/sMLQ2384blI?si=U4wmJwIrVp3LYCpe",
    categories: ["recording"],
    isRecording: true,
  },
  {
    id: 14,
    url: "https://youtu.be/-kwHjtVSnIg?si=KfMp_TaDLAmagGF0",
    categories: ["recording"],
    isRecording: true,
  },
];

/**
 * Gallery Images
 * 
 * Size is AUTO-DETECTED based on image dimensions:
 * - Landscape images (width > height) → full width row
 * - Portrait images (height >= width) → 2 per row
 * 
 * Optional properties:
 * - size: 'large' | 'medium' - Override auto-detection if needed
 * - focus: 'top' | 'center' | 'bottom' | 'cover'
 *   - 'top' = show top of image (best for portraits with faces)
 *   - 'center' = show center (default)
 *   - 'bottom' = show bottom
 *   - 'cover' = fit entire image in frame
 */
export const GALLERY_IMAGES = [
  { id: 1, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_1.jpeg", driveUrl: "https://drive.google.com/file/d/1V-o2tOVehs7tB7fW7yHVUNo9KQ73zPbZ/view", focus: "top" },
  { id: 2, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_2.jpeg", driveUrl: "https://drive.google.com/file/d/1Ko506jddSX4WEhnjKVTV7ZgEz59W6RxI/view", focus: "top" },
  { id: 3, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_3.JPG", driveUrl: "https://drive.google.com/file/d/1Y1PF83K8VZWCDMpYE7E2xVwmykoSrz0u/view", focus: "top" },
  { id: 4, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_4.jpeg", driveUrl: "https://drive.google.com/file/d/14H8Xz5JhnxEdyLj3gWQSJBypMbmyVj4G/view", focus: "top" },
  { id: 5, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_5.jpeg", driveUrl: "https://drive.google.com/file/d/1MctDyHI-X0CMb6mdV2QoviQpO0z4m1w_/view", focus: "top" },
  { id: 6, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_6.JPG", driveUrl: "https://drive.google.com/file/d/1YbYuwsAK13t9yoXPNouEYCVWr23oopDK/view", focus: "top" },
  { id: 7, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_7.JPG", driveUrl: "https://drive.google.com/file/d/1QTO7DUOrfNRSdBOFiNX3R9MnBMCckYHO/view", focus: "top" },
  { id: 8, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_8.jpg", driveUrl: "https://drive.google.com/file/d/1RxlGbfdj0PmyXM8a9HuWDmEUIsfD3PVd/view", focus: "top" },
  { id: 9, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_9.jpg", driveUrl: "https://drive.google.com/file/d/1qdxUT8y8TyJZX-gFGCMPgtjwQ9n6FiK6/view", focus: "top" },
  { id: 13, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_10.jpg", driveUrl: "https://drive.google.com/file/d/1TP-QVYr-AckRirzairoEnlpv0Ug31J-9/view", focus: "top" },
  { id: 10, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_11.jpg", driveUrl: "https://drive.google.com/file/d/1SGK2GB-PzITo8HaRRgnUj0zQRY1x8q4B/view", focus: "top" },
  { id: 11, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_12.jpeg", driveUrl: "https://drive.google.com/file/d/1phbknh14botuos4kIZZYcwFZ8G57Vs4L/view", focus: "top" },
  { id: 12, url: "https://matisaislive-public.s3.ap-south-1.amazonaws.com/images/matisa_13.JPG", driveUrl: "https://drive.google.com/file/d/12LbeBJO6r9VJ826dlwoLKB6hYkCeVM3r/view", focus: "top" },
]

// Featured performances shown on Shows page (limited preview)
export const PERFORMANCES = [
  YOUTUBE_VIDEOS[9],
  YOUTUBE_VIDEOS[0],
  YOUTUBE_VIDEOS[5],
  YOUTUBE_VIDEOS[10],
];

export const EVENT_TYPES = [
  { value: "wedding", label: "Wedding Ceremony" },
  { value: "reception", label: "Wedding Reception" },
  { value: "corporate", label: "Corporate Event" },
  { value: "private", label: "Private Party" },
  { value: "festival", label: "Festival / Public Gig" },
  { value: "lounge", label: "Restaurant / Live Lounge" },
  { value: "recording", label: "Recording / Studio Session" },
  { value: "other", label: "Other" },
];

export const TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah Jenkins",
    role: "Wedding Client",
    content: "Matisa made our wedding absolutely magical! Her voice is incredible and she learned our special song specifically for the ceremony.",
    rating: 5,
  },
  {
    id: 2,
    name: "Mark Thompson",
    role: "Event Horizon Inc.",
    content: "Professional, punctual, and the absolute highlight of our corporate gala. The energy was perfect for the networking hour.",
    rating: 5,
  },
  {
    id: 3,
    name: "David Rivera",
    role: "City Festival Director",
    content: "We hire Matisa every year for our summer festival. The crowd loves the energy and the setlist is always spot on.",
    rating: 5,
  },
];

export const FOOTER_LINKS = [
  { label: "Contact/Booking", href: "/enquiry" },
];

// Clientele - Premium/Famous venues (Names you may know)
export const FEATURED_CLIENTS = [
  {
    id: 1,
    name: "Taj Hotels",
    location: "Mumbai",
    venues: ["Taj Trees", "Taj Presidency"],
    logo: "https://upload.wikimedia.org/wikipedia/commons/c/cd/Taj_Hotels_logo.svg",
  },
  {
    id: 2,
    name: "Marriott",
    location: "Navi Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/b3/Marriott_hotels_logo14.svg",
  },
  {
    id: 3,
    name: "Grand Hyatt",
    location: "Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/7/7a/Grand_Hyatt_logo.svg",
  },
  {
    id: 4,
    name: "Sofitel",
    location: "Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/0/0c/Sofitel-logo.jpg",
  },
  {
    id: 5,
    name: "The Westin",
    location: "Powai, Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/c/cc/Westin_Hotels_Logo.svg",
  },
  {
    id: 6,
    name: "The Park",
    location: "Navi Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/4/4a/The_Park_Hotels.jpg",
  },
  {
    id: 7,
    name: "Jio World Plaza",
    location: "Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/Reliance_Jio_Logo.svg/2560px-Reliance_Jio_Logo.svg.png",
  },
  {
    id: 8,
    name: "Phoenix Mall",
    location: "Mumbai",
    logo: "https://upload.wikimedia.org/wikipedia/commons/c/c6/Phoenix_Marketcity_Logo.png",
  },
];

// Other amazing venues
export const OTHER_CLIENTS = [
  // Malls & Event Spaces
  { id: 101, name: "R City Mall", location: "Mumbai", type: "mall", logo: "https://upload.wikimedia.org/wikipedia/commons/c/c2/R_City_Mall_Ghatkopar.jpg" },
  { id: 102, name: "Koram Mall", location: "Thane", type: "mall", logo: "https://upload.wikimedia.org/wikipedia/commons/1/1b/Korum_Mall.JPG" },
  { id: 103, name: "Orbit Mall", location: "Navi Mumbai", type: "mall", logo: "https://upload.wikimedia.org/wikipedia/commons/1/1a/In_Orbit_Mall.jpg" },
  { id: 104, name: "NESCO", location: "Goregaon, Mumbai", type: "venue", logo: "https://www.nesco.in/resources/images/logo-white.png" },

  // Stays & Resorts
  { id: 105, name: "Luma Stays", location: "Alibaug", type: "resort", logo: "" },
  { id: 106, name: "Lakeside Boheme Inn", location: "Karjat", type: "resort", logo: "" },
  { id: 107, name: "Zostel", location: "Gokarna", type: "hostel", logo: "https://upload.wikimedia.org/wikipedia/commons/f/fe/ZOSTEL_LOGO_PNG_%281%29.png" },

  // Restaurants & Lounges - Mumbai
  { id: 108, name: "Kandil", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 109, name: "1BHK", location: "Mumbai", type: "lounge", logo: "" },
  { id: 110, name: "The Corkage", location: "Mumbai", type: "bar", logo: "" },
  { id: 111, name: "Madeira & Mime", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 112, name: "Coast & Bloom", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 113, name: "Grey Cravings", location: "Mumbai", type: "cafe", logo: "" },
  { id: 114, name: "Beer Cafe", location: "Mumbai", type: "bar", logo: "https://thebeercafe.com/wp-content/uploads/2023/03/the-beer-cafe-logo.png" },
  { id: 115, name: "Ishara", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 116, name: "Maujja", location: "Mumbai", type: "lounge", logo: "" },
  { id: 117, name: "Golden Leaf Banquet", location: "Mumbai", type: "banquet", logo: "" },
  { id: 118, name: "Nusara", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 119, name: "Silkroute", location: "Mumbai", type: "restaurant", logo: "" },
  { id: 120, name: "Hapi Beer", location: "Mumbai", type: "bar", logo: "" },
  { id: 121, name: "Golden Lawns", location: "Mumbai", type: "venue", logo: "" },
  { id: 122, name: "Blackberry", location: "Mumbai", type: "restaurant", logo: "" },

  // Thane
  { id: 123, name: "Beer Cafe", location: "Thane", type: "bar", logo: "https://thebeercafe.com/wp-content/uploads/2023/03/the-beer-cafe-logo.png" },
  { id: 124, name: "Pink Martini", location: "Thane", type: "lounge", logo: "" },
  { id: 125, name: "La Coco", location: "Thane", type: "restaurant", logo: "" },
  { id: 126, name: "Vintage Garden", location: "Thane", type: "restaurant", logo: "" },
  { id: 127, name: "NXT LVL", location: "Thane", type: "lounge", logo: "" },
  { id: 128, name: "Courtyard", location: "Thane", type: "restaurant", logo: "" },
  { id: 129, name: "Malang", location: "Thane", type: "lounge", logo: "" },
  { id: 130, name: "Maujja", location: "Thane", type: "lounge", logo: "" },
  { id: 131, name: "Angrezi Patiyala", location: "Thane", type: "restaurant", logo: "" },
  { id: 132, name: "The Cabo Cruise", location: "Thane", type: "lounge", logo: "" },
  { id: 133, name: "Pink Baba", location: "Thane", type: "lounge", logo: "" },
  { id: 134, name: "Area 51", location: "Thane", type: "lounge", logo: "" },
  { id: 135, name: "Satkar Residency", location: "Thane", type: "hotel", logo: "" },
  { id: 136, name: "Raymond", location: "Thane", type: "venue", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/Raymond_Limited_Logo.svg/2560px-Raymond_Limited_Logo.svg.png" },
  { id: 137, name: "Shelter Farms", location: "Thane", type: "venue", logo: "" },
  { id: 138, name: "Skyline", location: "Thane", type: "lounge", logo: "" },
  { id: 139, name: "Mojito", location: "Thane", type: "bar", logo: "" },
  { id: 140, name: "Mato Mato", location: "Thane", type: "restaurant", logo: "" },
  { id: 141, name: "Blanch", location: "Thane", type: "restaurant", logo: "" },
  { id: 142, name: "Thane Bistro", location: "Thane", type: "restaurant", logo: "" },
  { id: 143, name: "Inventia", location: "Thane", type: "venue", logo: "" },
  { id: 144, name: "Jamun Tree", location: "Thane", type: "restaurant", logo: "" },
  { id: 145, name: "Farmhouse", location: "Thane", type: "venue", logo: "" },
  { id: 146, name: "Royal Meridian", location: "Thane", type: "hotel", logo: "" },
  { id: 147, name: "Melange", location: "Thane", type: "restaurant", logo: "" },
  { id: 148, name: "Radiolily", location: "Thane", type: "lounge", logo: "" },
  { id: 149, name: "Chulha", location: "Thane", type: "restaurant", logo: "" },

  // Navi Mumbai
  { id: 150, name: "Beer Cafe", location: "Navi Mumbai", type: "bar", logo: "https://thebeercafe.com/wp-content/uploads/2023/03/the-beer-cafe-logo.png" },
  { id: 151, name: "Brew House", location: "Navi Mumbai", type: "bar", logo: "" },
  { id: 152, name: "Canvas", location: "Navi Mumbai", type: "lounge", logo: "" },
  { id: 153, name: "Pier One", location: "Navi Mumbai", type: "restaurant", logo: "" },
  { id: 154, name: "Atarangii Skylounge", location: "Navi Mumbai", type: "lounge", logo: "" },
  { id: 155, name: "Atarangii House", location: "Navi Mumbai", type: "lounge", logo: "" },
  { id: 156, name: "Tom Tom", location: "Navi Mumbai", type: "restaurant", logo: "" },
  { id: 157, name: "The Empire", location: "Navi Mumbai", type: "restaurant", logo: "" },
  { id: 158, name: "Broaster Chicken", location: "Navi Mumbai", type: "restaurant", logo: "" },

  // Other locations
  { id: 159, name: "Indigo Spice", location: "Dombivli", type: "restaurant", logo: "" },
  { id: 160, name: "JNPT Association", location: "Uran", type: "venue", logo: "" },
  { id: 161, name: "Tandoori", location: "Honnavar", type: "restaurant", logo: "" },
  { id: 162, name: "Green Valley Lakeview", location: "Honnavar", type: "resort", logo: "" },
  { id: 163, name: "Fitur", location: "Goa", type: "restaurant", logo: "" },
];

// Recording / Studio Content
export const RECORDING_CONTENT = {
  title: "Matisa Records",
  subtitle: "Beyond the Stage",
  tagline: "Where melodies come to life in the studio",
  description: "Beyond live performances, Matisa is also a versatile playback singer and recording artist. Her voice has been featured in various music productions, bringing emotion and depth to every track she touches.",
  services: [
    {
      icon: "mic",
      title: "Playback Singing",
      description: "Professional voice for films, ads & media projects",
    },
    {
      icon: "album",
      title: "Studio Recording",
      description: "High-quality vocals for your music productions",
    },
    {
      icon: "music_note",
      title: "Cover Songs",
      description: "Soulful renditions of your favorite tracks",
    },
    {
      icon: "headphones",
      title: "Jingles & Ads",
      description: "Catchy vocals for commercials & brand content",
    },
  ],
  cta: {
    primary: "Collaborate with Matisa",
    secondary: "Listen to Recordings",
  },
};

// Recording videos (subset of YOUTUBE_VIDEOS with isRecording flag)
export const RECORDING_VIDEOS = YOUTUBE_VIDEOS.filter(v => v.isRecording);

// Images
export const IMAGES = {
  heroBackground: "/hero_main.png",
  aboutPortrait: "/hero_1.jpg",
  enquiryBackground: "/hero_main.png",
};
